"use client";

/**
 * Stack Drawer Hook
 * 
 * Provides a backward-compatible hook for drawer management that can work
 * with both the new global stack system and the legacy individual drawer system.
 * This allows for gradual migration without breaking existing components.
 */

import * as React from "react";
import { useGlobalDrawerStack } from "../components/global-drawer-stack";

interface UseStackDrawerOptions {
  drawerId: string;
  fallbackToLocal?: boolean; // If true, falls back to local state if global stack not available
}

interface UseStackDrawerReturn {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onToggle: () => void;
  // For compatibility with existing StandardizedDrawer components
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const useStackDrawer = ({ 
  drawerId, 
  fallbackToLocal = true 
}: UseStackDrawerOptions): UseStackDrawerReturn => {
  // Try to use global stack first
  const [useGlobalStack, setUseGlobalStack] = React.useState(true);
  const [localOpen, setLocalOpen] = React.useState(false);
  
  let globalStack: ReturnType<typeof useGlobalDrawerStack> | null = null;
  let isGlobalDrawerOpen = false;
  let globalOpenDrawer: (() => void) | null = null;
  let globalCloseDrawer: (() => void) | null = null;
  
  try {
    if (useGlobalStack) {
      globalStack = useGlobalDrawerStack();
      isGlobalDrawerOpen = globalStack.isDrawerOpen(drawerId as any);
      globalOpenDrawer = () => globalStack!.openDrawer(drawerId as any);
      globalCloseDrawer = () => globalStack!.closeDrawer(drawerId as any);
    }
  } catch (error) {
    // Global stack not available, fall back to local state
    if (fallbackToLocal) {
      setUseGlobalStack(false);
    } else {
      throw error;
    }
  }
  
  // Use global stack if available, otherwise use local state
  const isOpen = useGlobalStack && globalStack ? isGlobalDrawerOpen : localOpen;
  
  const onOpen = React.useCallback(() => {
    if (useGlobalStack && globalOpenDrawer) {
      globalOpenDrawer();
    } else {
      setLocalOpen(true);
    }
  }, [useGlobalStack, globalOpenDrawer]);
  
  const onClose = React.useCallback(() => {
    if (useGlobalStack && globalCloseDrawer) {
      globalCloseDrawer();
    } else {
      setLocalOpen(false);
    }
  }, [useGlobalStack, globalCloseDrawer]);
  
  const onToggle = React.useCallback(() => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  }, [isOpen, onOpen, onClose]);
  
  const onOpenChange = React.useCallback((open: boolean) => {
    if (open) {
      onOpen();
    } else {
      onClose();
    }
  }, [onOpen, onClose]);
  
  return {
    isOpen,
    onOpen,
    onClose,
    onToggle,
    // For compatibility with existing StandardizedDrawer components
    open: isOpen,
    onOpenChange,
  };
};
